import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { IUser, DEACTIVATION_STATUS } from '../../../@core/models/model';
import { AvatarStackComponent } from '../avatar-stack/avatar-stack.component';

@Component({
  selector: 'app-user-name-display',
  standalone: true,
  imports: [CommonModule, TooltipModule, AvatarStackComponent],
  template: `
    <span class="inline-flex items-center gap-1" [ngClass]="class">

      @if (variant === 'avatar' || variant === 'avatar-name') {
        <app-avatar-stack
          [users]="userAsArray"
          [visibleAvatars]="1"
          [size]="size">
        </app-avatar-stack>
      }

      @if (variant === 'name' || variant === 'avatar-name') {
        @if (isDeactivated()) {
          <span [pTooltip]="tooltipText" [tooltipPosition]="tooltipPosition" class="inline-block">
            <span class="line-through text-gray-500" [ngClass]="textClass">
              {{ displayName() }}
            </span>
          </span>
        } @else {
          <span [ngClass]="textClass">{{ displayName() }}</span>
        }
      }

    </span>
  `,
  styles: [`
    :host {
      display: inline;
    }
  `]
})
export class UserNameDisplayComponent {
  @Input() user: IUser | Partial<IUser> | string | null | undefined;
  @Input() variant: 'name' | 'avatar' | 'avatar-name' = 'name';
  @Input() tooltipText: string = 'Account deactivated';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
  @Input() fallbackText: string = '';
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' = 'sm';
  @Input() class: string = '';

  get userAsArray(): Partial<IUser>[] {
    if (!this.user || typeof this.user === 'string') return [{}];
    return [this.user as Partial<IUser>];
  }

  get textClass(): string {
    const map: Record<UserNameDisplayComponent['size'], string> = {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };
    return map[this.size];
  }

  displayName(): string {
    if (!this.user) return this.fallbackText;
    if (typeof this.user === 'string') return this.fallbackText;

    const userObj = this.user as IUser;

    if (userObj.fullName) return userObj.fullName;

    const firstName = userObj.firstName?.trim() || '';
    const lastName = userObj.lastName?.trim() || '';

    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    if (userObj.email) return userObj.email;

    return this.fallbackText;
  }

  isDeactivated(): boolean {
    if (!this.user || typeof this.user === 'string') return false;
    const userObj = this.user as IUser;
    return userObj.active === false || userObj.deactivationStatus === DEACTIVATION_STATUS.DEACTIVATED;
  }
}
